/**
 * mermaidJsonConverter.ts
 *
 * Converts structured diagram JSON (produced by the LLM) into valid Mermaid syntax.
 * Port of the Python `json_to_mermaid` module — pure string manipulation, no dependencies.
 */

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface FlowchartNode {
  id: string;
  label?: string;
  shape?: string;
  class?: string;
  fill?: string;
  stroke?: string;
  textColor?: string;
}

interface FlowchartEdge {
  from: string;
  to: string;
  label?: string;
  style?: "solid" | "dashed";
  class?: string;
  stroke?: string;
  textColor?: string;
}

interface Subgraph {
  id?: string;
  label?: string;
  nodes?: string[];
}

interface FlowchartDiagram {
  diagram_type: "flowchart";
  direction?: string;
  nodes?: FlowchartNode[];
  edges?: FlowchartEdge[];
  subgraphs?: Subgraph[];
}

interface Participant {
  id: string;
  label?: string;
}

interface Message {
  from: string;
  to: string;
  label?: string;
  type?: "solid" | "dashed";
}

interface SequenceDiagram {
  diagram_type: "sequence";
  participants?: Participant[];
  messages?: Message[];
}

interface State {
  id: string;
  label?: string;
}

interface Transition {
  from: string;
  to: string;
  label?: string;
}

interface StateDiagram {
  diagram_type: "state";
  states?: State[];
  transitions?: Transition[];
}

interface GanttTask {
  name: string;
  start?: string;
  duration?: string;
  status?: string;
  after?: string;
}

interface GanttSection {
  name?: string;
  tasks?: GanttTask[];
}

interface GanttDiagram {
  diagram_type: "gantt";
  title?: string;
  date_format?: string;
  sections?: GanttSection[];
}

interface PieItem {
  label: string;
  value: number;
}

interface PieDiagram {
  diagram_type: "pie";
  title?: string;
  show_data?: boolean;
  items?: PieItem[];
}

interface GitAction {
  action: "commit" | "branch" | "checkout" | "merge" | "cherry-pick";
  name?: string;
  message?: string;
  tag?: string;
  commit_id?: string;
}

interface GitgraphDiagram {
  diagram_type: "gitgraph";
  commits?: GitAction[];
}

interface MindmapNode {
  label: string;
  shape?: string;
  children?: MindmapNode[];
}

interface MindmapDiagram {
  diagram_type: "mindmap";
  root?: MindmapNode;
}

interface TimelinePeriod {
  label: string;
  events?: string[];
}

interface TimelineDiagram {
  diagram_type: "timeline";
  title?: string;
  periods?: TimelinePeriod[];
}

type DiagramData =
  | FlowchartDiagram
  | SequenceDiagram
  | StateDiagram
  | GanttDiagram
  | PieDiagram
  | GitgraphDiagram
  | MindmapDiagram
  | TimelineDiagram;

interface ConversionResult {
  code: string;
  error: string | null;
}

// ─────────────────────────────────────────────────────────────
// Reserved words & shape map
// ─────────────────────────────────────────────────────────────

const MERMAID_RESERVED = new Set([
  "end",
  "graph",
  "subgraph",
  "direction",
  "click",
  "style",
  "classDef",
  "class",
  "linkStyle",
  "default",
]);

const SHAPE_MAP: Record<string, [string, string]> = {
  box: ['["', '"]'],
  round: ['(["', '"])'],
  diamond: ['{"', '"}'],
  circle: ['(("', '"))'],
  hexagon: ['{{"', '"}}'],
  parallelogram: ['[/"', '"/]'],
  cylinder: ['[("', '")]'],
  stadium: ['(["', '"])'],
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function safeId(nodeId: string): string {
  let safe = nodeId.replace(/[^a-zA-Z0-9_]/g, "_");
  if (MERMAID_RESERVED.has(safe.toLowerCase())) {
    safe = `nd_${safe}`;
  }
  if (safe && !/^[a-zA-Z]/.test(safe)) {
    safe = `n${safe}`;
  }
  return safe;
}

function safeLabel(label: string): string {
  return label.replace(/"/g, "'");
}

// ─────────────────────────────────────────────────────────────
// Builders
// ─────────────────────────────────────────────────────────────

// Default class palette using MB Energy & enport corporate colors
// See: MB Light Green #00a66d, MB Digital Green #00e68c,
// MB Orange #f07000, MB Dark/Deep Greens, Anthracite, Grays.
const CLASS_PALETTE: Record<string, string> = {
  // Success / positive
  success: "fill:#00e68c,stroke:#00a66d,color:#003226", // digital + light + deep green
  // Warning / attention
  warning: "fill:#fff3e0,stroke:#f07000,color:#f07000", // light orange bg, brand orange border/text
  // Error / critical – use orange as main signal, darker border
  error: "fill:#ffebee,stroke:#f07000,color:#003226",
  // Informational / neutral
  info: "fill:#e0e1df,stroke:#50585f,color:#003226", // light gray bg, mid gray border, deep green text
  // Accent / interactive
  accent: "fill:#00a66d,stroke:#003226,color:#ffffff", // light green with deep green border, white text
  // Generic default node
  default: "fill:#ffffff,stroke:#50585f,color:#24242a",
};

function buildFlowchart(data: FlowchartDiagram): string {
  const direction = data.direction || "TD";
  const lines: string[] = [`flowchart ${direction}`];

  const idMap: Record<string, string> = {};
  for (const node of data.nodes || []) {
    idMap[node.id] = safeId(node.id);
  }

  const subgraphNodes = new Set<string>();
  const usedClasses = new Set<string>();
  const nodeClasses: string[] = [];
  const nodeStyles: string[] = [];

  for (const sg of data.subgraphs || []) {
    const sgId = safeId(sg.id || `sg_${sg.label || "group"}`);
    const sgLabel = safeLabel(sg.label || "");
    lines.push(`    subgraph ${sgId}["${sgLabel}"]`);

    for (const nodeRef of sg.nodes || []) {
      const mapped = idMap[nodeRef] || safeId(nodeRef);
      const nodeData = (data.nodes || []).find((n) => n.id === nodeRef);
      if (nodeData) {
        const label = safeLabel(nodeData.label || nodeRef);
        const shape = nodeData.shape || "box";
        const [left, right] = SHAPE_MAP[shape] || SHAPE_MAP["box"];
        lines.push(`        ${mapped}${left}${label}${right}`);
        subgraphNodes.add(nodeRef);
      }
    }

    lines.push("    end");
  }

  for (const node of data.nodes || []) {
    if (!subgraphNodes.has(node.id)) {
      const safe = idMap[node.id] || safeId(node.id);
      const label = safeLabel(node.label || node.id);
      const shape = node.shape || "box";
      const [left, right] = SHAPE_MAP[shape] || SHAPE_MAP["box"];
      lines.push(`    ${safe}${left}${label}${right}`);
    }
  }

  // Collect class assignments and inline styles for ALL nodes
  for (const node of data.nodes || []) {
    const safe = idMap[node.id] || safeId(node.id);

    if (node.class) {
      usedClasses.add(node.class);
      nodeClasses.push(`    class ${safe} ${node.class}`);
    }

    const styleParts: string[] = [];
    if (node.fill) styleParts.push(`fill:${node.fill}`);
    if (node.stroke) styleParts.push(`stroke:${node.stroke}`);
    if (node.textColor) styleParts.push(`color:${node.textColor}`);
    if (styleParts.length) {
      nodeStyles.push(`    style ${safe} ${styleParts.join(",")}`);
    }
  }

  const edgeStyles: string[] = [];
  let edgeIndex = 0;
  for (const edge of data.edges || []) {
    const fromId = idMap[edge.from] || safeId(edge.from);
    const toId = idMap[edge.to] || safeId(edge.to);
    const edgeLabel = edge.label || "";
    const style = edge.style || "solid";
    const arrow = style === "dashed" ? "-..->" : "-->";

    if (edgeLabel) {
      lines.push(`    ${fromId} ${arrow}|"${safeLabel(edgeLabel)}"| ${toId}`);
    } else {
      lines.push(`    ${fromId} ${arrow} ${toId}`);
    }

    const edgeStyleParts: string[] = [];
    if (edge.stroke) edgeStyleParts.push(`stroke:${edge.stroke}`);
    if (edge.textColor) edgeStyleParts.push(`color:${edge.textColor}`);
    if (edgeStyleParts.length) {
      edgeStyles.push(`    linkStyle ${edgeIndex} ${edgeStyleParts.join(",")}`);
    }

    edgeIndex += 1;
  }

  // Emit classDefs
  if (usedClasses.size > 0) {
    lines.push("");
    for (const cls of usedClasses) {
      const style = CLASS_PALETTE[cls] || CLASS_PALETTE.default;
      lines.push(`    classDef ${cls} ${style}`);
    }
  }

  // Emit class assignments
  if (nodeClasses.length) {
    lines.push(...nodeClasses);
  }

  // Emit inline styles
  if (nodeStyles.length) {
    lines.push(...nodeStyles);
  }

  if (edgeStyles.length) {
    lines.push(...edgeStyles);
  }

  return lines.join("\n");
}

function buildSequence(data: SequenceDiagram): string {
  const lines: string[] = ["sequenceDiagram"];

  for (const p of data.participants || []) {
    const safe = safeId(p.id);
    const label = safeLabel(p.label || p.id);
    lines.push(`    participant ${safe} as "${label}"`);
  }
  for (const msg of data.messages || []) {
    const fromId = safeId(msg.from);
    const toId = safeId(msg.to);
    const label = safeLabel(msg.label || "");
    const arrow = (msg.type || "solid") === "solid" ? "->>" : "-->>";
    lines.push(`    ${fromId}${arrow}${toId}: ${label}`);
  }

  return lines.join("\n");
}

function buildState(data: StateDiagram): string {
  const lines: string[] = ["stateDiagram-v2"];
  for (const state of data.states || []) {
    const safe = safeId(state.id);
    const label = safeLabel(state.label || state.id);
    lines.push(`    ${safe} : "${label}"`);
  }

  for (const trans of data.transitions || []) {
    const fromSafe = trans.from === "[*]" ? "[*]" : safeId(trans.from);
    const toSafe = trans.to === "[*]" ? "[*]" : safeId(trans.to);
    const label = trans.label || "";
    if (label) {
      lines.push(`    ${fromSafe} --> ${toSafe} : ${safeLabel(label)}`);
    } else {
      lines.push(`    ${fromSafe} --> ${toSafe}`);
    }
  }

  return lines.join("\n");
}

function buildGantt(data: GanttDiagram): string {
  const lines: string[] = ["gantt"];

  if (data.title) {
    lines.push(`    title ${safeLabel(data.title)}`);
  }
  lines.push(`    dateFormat ${data.date_format || "YYYY-MM-DD"}`);

  for (const section of data.sections || []) {
    lines.push(`    section ${safeLabel(section.name || "")}`);

    for (const task of section.tasks || []) {
      const taskName = safeLabel(task.name || "");
      const status = task.status || "";
      const start = task.start || "";
      const duration = task.duration || "1d";
      const after = task.after || "";

      const parts: string[] = [`    ${taskName}`];
      parts.push(status ? ` :${status},` : " :");
      if (after) {
        parts.push(` after ${safeId(after)},`);
      } else if (start) {
        parts.push(` ${start},`);
      }
      parts.push(` ${duration}`);
      lines.push(parts.join(""));
    }
  }

  return lines.join("\n");
}

function buildPie(data: PieDiagram): string {
  const lines: string[] = [data.show_data !== false ? "pie showData" : "pie"];

  if (data.title) {
    lines.push(`    title ${safeLabel(data.title)}`);
  }

  for (const item of data.items || []) {
    lines.push(`    "${safeLabel(item.label || "")}" : ${item.value || 0}`);
  }

  return lines.join("\n");
}

function buildGitgraph(data: GitgraphDiagram): string {
  const lines: string[] = ["gitGraph"];

  for (const action of data.commits || []) {
    const act = action.action || "commit";
    const name = action.name || "";
    const message = action.message || "";
    const tag = action.tag || "";

    switch (act) {
      case "commit": {
        const parts: string[] = ["    commit"];
        if (message) parts.push(` id: "${safeLabel(message)}"`);
        if (tag) parts.push(` tag: "${safeLabel(tag)}"`);
        lines.push(parts.join(""));
        break;
      }
      case "branch":
        lines.push(`    branch ${safeId(name)}`);
        break;
      case "checkout":
        lines.push(`    checkout ${safeId(name)}`);
        break;
      case "merge": {
        const parts: string[] = [`    merge ${safeId(name)}`];
        if (message) parts.push(` id: "${safeLabel(message)}"`);
        if (tag) parts.push(` tag: "${safeLabel(tag)}"`);
        lines.push(parts.join(""));
        break;
      }
      case "cherry-pick": {
        const commitId = action.commit_id || "";
        if (commitId) {
          lines.push(`    cherry-pick id: "${safeLabel(commitId)}"`);
        }
        break;
      }
    }
  }

  return lines.join("\n");
}

function buildMindmap(data: MindmapDiagram): string {
  const lines: string[] = ["mindmap"];

  function addNode(node: MindmapNode, depth: number = 1): void {
    const indent = "  ".repeat(depth);
    const label = node.label || "";
    const shape = node.shape || "default";

    switch (shape) {
      case "square":
        lines.push(`${indent}[${safeLabel(label)}]`);
        break;
      case "rounded":
        lines.push(`${indent}(${safeLabel(label)})`);
        break;
      case "circle":
        lines.push(`${indent}((${safeLabel(label)}))`);
        break;
      case "cloud":
        lines.push(`${indent})${safeLabel(label)}(`);
        break;
      case "hexagon":
        lines.push(`${indent}{{${safeLabel(label)}}}`);
        break;
      default:
        lines.push(`${indent}${safeLabel(label)}`);
    }

    for (const child of node.children || []) {
      addNode(child, depth + 1);
    }
  }

  if (data.root) {
    addNode(data.root);
  }

  return lines.join("\n");
}

function buildTimeline(data: TimelineDiagram): string {
  const lines: string[] = ["timeline"];

  if (data.title) {
    lines.push(`    title ${safeLabel(data.title)}`);
  }

  for (const period of data.periods || []) {
    lines.push(`    ${safeLabel(period.label || "")}`);
    for (const event of period.events || []) {
      lines.push(`        : ${safeLabel(event)}`);
    }
  }

  return lines.join("\n");
}

// ─────────────────────────────────────────────────────────────
// Main converter
// ─────────────────────────────────────────────────────────────

const BUILDERS: Record<string, (data: any) => string> = {
  flowchart: buildFlowchart,
  sequence: buildSequence,
  state: buildState,
  gantt: buildGantt,
  pie: buildPie,
  gitgraph: buildGitgraph,
  mindmap: buildMindmap,
  timeline: buildTimeline,
};

/**
 * Convert a structured diagram JSON object to valid Mermaid syntax.
 *
 * @param data - Parsed JSON with a `diagram_type` field
 * @returns `{ code, error }` — code is the mermaid string, error is null on success
 */
export function jsonToMermaid(data: DiagramData): ConversionResult {
  try {
    const diagramType = data.diagram_type || "flowchart";
    const builder = BUILDERS[diagramType];

    if (!builder) {
      return {
        code: "",
        error: `Unknown diagram type: '${diagramType}'. Supported: ${Object.keys(BUILDERS).join(", ")}`,
      };
    }

    return { code: builder(data), error: null };
  } catch (err: any) {
    return {
      code: "",
      error: `Failed to build mermaid from JSON: ${err?.message || String(err)}`,
    };
  }
}

/**
 * Check if a string is diagram JSON (has a `diagram_type` field).
 * Returns the parsed object if yes, null if not.
 */
export function parseDiagramJson(code: string): DiagramData | null {
  try {
    const parsed = JSON.parse(code);
    if (parsed && typeof parsed === "object" && parsed.diagram_type) {
      return parsed as DiagramData;
    }
  } catch {
    // Not JSON — that's fine, it's raw mermaid
  }
  return null;
}
